import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getLocations } from 'services/locations/redux';
import { Location } from 'services/locations';
import { TransferOrderType } from 'services/transferOrders';

interface UseTransferOrderTypeProps {
  sourceLocationId: number | null;
  destinationLocationId: number | null;
}

interface UseTransferOrderTypeResult {
  transferType: TransferOrderType | null;
  isLocationSelectionComplete: boolean;
}

const findRootLocationId = (location: Location | undefined): number | null => {
  if (!location?.id) return null;
  return location.rootLocationId ?? location.id;
};

export const useTransferOrderType = ({
  sourceLocationId,
  destinationLocationId,
}: UseTransferOrderTypeProps): UseTransferOrderTypeResult => {
  const locationsState = useSelector(getLocations);

  const result = useMemo(() => {
    // Check if both locations are selected
    const isLocationSelectionComplete =
      sourceLocationId != null && destinationLocationId != null;

    if (!isLocationSelectionComplete) {
      return {
        transferType: null,
        isLocationSelectionComplete: false,
      };
    }

    // Find the selected locations from the locations items
    const locations = locationsState.items || [];
    const sourceLocation = locations.find(
      (loc: Location) => loc.id === sourceLocationId
    );
    const destinationLocation = locations.find(
      (loc: Location) => loc.id === destinationLocationId
    );

    // If either location isn't in store yet, we cannot determine type reliably.
    if (!sourceLocation || !destinationLocation) {
      return {
        transferType: null,
        isLocationSelectionComplete: false,
      };
    }

    // Get root location IDs to determine transfer type
    const sourceRootId = findRootLocationId(sourceLocation);
    const destinationRootId = findRootLocationId(destinationLocation);

    // Determine transfer type based on root location comparison
    const transferType =
      sourceRootId === destinationRootId
        ? TransferOrderType.MTO
        : TransferOrderType.STO;

    return {
      transferType,
      isLocationSelectionComplete: true,
    };
  }, [sourceLocationId, destinationLocationId, locationsState.items]);

  return result;
};
